// Diagnosis and alerts for the panel: Spanish labels, the text of each finding and of each alert (built from the
// structured fields of the API, not from its English titles) and how rules read.

import type { Alert, AlertRule, Finding, ScoreArea, ScoreItem } from '@growth/client';
import { signalEs } from './components/ua.tsx';
import { DASH, fmtDay, fmtDuration, fmtMoney, fmtNum, fmtPct } from './format.ts';

export const AREA_LABEL: Record<ScoreArea, string> = {
  marketability: 'Atractivo',
  acquisition: 'Adquisición',
  activation: 'Activación',
  engagement: 'Engagement',
  retention: 'Retención',
  monetization: 'Monetización',
  economics: 'Economía',
  scale: 'Escala',
};

export const AREA_HINT: Record<ScoreArea, string> = {
  marketability: '¿Cuánto cuesta un jugador?',
  acquisition: '¿El anuncio y la ficha convencen?',
  activation: '¿Los nuevos llegan a jugar?',
  engagement: '¿Cuánto juegan?',
  retention: '¿Vuelven?',
  monetization: '¿Generan ingresos?',
  economics: '¿Devuelven lo que cuestan?',
  scale: '¿Se puede invertir más?',
};

export const METRIC_ES: Record<string, string> = {
  cpi: 'CPI',
  ctr: 'CTR',
  ipm: 'IPM',
  install_cvr: 'Conversión clic → instalación',
  store_cvr: 'Conversión de la ficha (Play)',
  tutorial: 'Tutorial completado',
  playtime_per_dau: 'Tiempo de juego por DAU',
  d1: 'Retención D1',
  d7: 'Retención D7',
  d30: 'Retención D30',
  arpdau: 'ARPDAU',
  ltv_d7: 'LTV D7',
  cpi_ltv_d30: 'CPI / LTV D30',
  roas_d7: 'ROAS D7',
  roas_d30: 'ROAS D30',
  installs: 'Jugadores nuevos',
  dau: 'DAU',
  revenue: 'Ingresos',
  crash_rate: 'Tasa de crashes',
};

export const STATUS_LABEL: Record<string, string> = { green: 'Verde', yellow: 'Amarillo', red: 'Rojo', no_target: 'Sin objetivo', no_data: 'Sin datos', insufficient: 'Pocos datos' };

export const SEVERITY: Record<string, { label: string; icon: 'critical' | 'warning' | 'good' | 'info' }> = {
  critical: { label: 'Crítica', icon: 'critical' },
  warning: { label: 'Aviso', icon: 'warning' },
  opportunity: { label: 'Oportunidad', icon: 'good' },
  info: { label: 'Info', icon: 'info' },
};

export const FINDING: Record<string, { title: string; text: string }> = {
  data_quality: { title: 'Datos con errores', text: 'Data Health tiene errores: corrígelos antes de decidir nada. Con analítica rota cualquier conclusión puede ser falsa.' },
  weak_hook: { title: 'Gancho débil', text: 'CTR bajo: la creatividad (el gancho, los primeros segundos) no llama la atención. Prueba conceptos claramente distintos.' },
  store_mismatch: {
    title: 'La ficha no convierte',
    text: 'Los anuncios consiguen clics pero pocos instalan: revisa que el icono, las capturas y el vídeo de la ficha cuenten lo mismo que el anuncio.',
  },
  low_quality_traffic: {
    title: 'Tráfico barato que no se queda',
    text: 'CPI bueno pero D1 malo: llegan jugadores baratos que no vuelven. Revisa la segmentación y la primera partida.',
  },
  expensive_acquisition: {
    title: 'Retiene, pero captar es caro',
    text: 'D1 bueno pero CPI alto: quien llega se queda; las creatividades o la segmentación salen caras. Prueba creatividades nuevas.',
  },
  monetization_gap: { title: 'Revisa la monetización', text: 'CPI y retención buenos pero ingresos bajos: revisa dónde y cuántos anuncios se ven, las ofertas y los precios.' },
  onboarding: { title: 'Problema de onboarding', text: 'Muchos jugadores nuevos no terminan el tutorial: revisa los primeros minutos.' },
  scale_candidate: {
    title: 'Candidato a escalar',
    text: 'Los jugadores devuelven lo que cuestan: se puede invertir más, con subidas graduales y vigilando el CPI (la plataforma nunca toca presupuestos).',
  },
};

export const findingTitle = (f: Pick<Finding, 'id' | 'summary'>) => FINDING[f.id]?.title ?? f.id;
export const findingText = (f: Pick<Finding, 'id' | 'summary'>) => FINDING[f.id]?.text ?? f.summary;

/** "data:cpi" -> "faltan datos de CPI" */
export function missingText(m: string): string {
  const [kind, metric = ''] = m.split(':');
  const name = METRIC_ES[metric] ?? metric;
  if (kind === 'target') return `sin objetivo de ${name}`;
  if (kind === 'sample') return `pocos jugadores en ${name}`;
  return `faltan datos de ${name}`;
}

/** A scorecard value in its unit. */
export function scoreValue(s: Pick<ScoreItem, 'unit' | 'metric'>, v: number | null | undefined, ccy: string): string {
  if (v === null || v === undefined) return DASH;
  switch (s.unit) {
    case 'rate':
      return fmtPct(v, s.metric === 'ctr' ? 2 : 1);
    case 'money':
      return fmtMoney(v, ccy, s.metric === 'arpdau' ? 4 : 3);
    case 'seconds':
      return fmtDuration(v);
    case 'days':
      return `${fmtNum(v, 0)} días`;
    default:
      return s.metric.startsWith('roas') ? fmtPct(v, 0) : fmtNum(v, 2);
  }
}

// ---------------------------------------------------------------- alerts

/** Spanish titles of the Data Health checks (the API keeps English texts; details stay as returned). */
export const HEALTH_TITLE: Record<string, string> = {
  analytics_stopped: 'Llegan eventos',
  ingest_volume: 'Eventos recibidos en el periodo',
  duplicates: 'Eventos duplicados (reintentos del SDK)',
  rejected_events: 'Eventos rechazados',
  invalid_params: 'Eventos con parámetros inválidos',
  unknown_event_schema: 'Builds que envían eventos fuera del esquema',
  future_timestamps: 'Fechas en el futuro',
  invalid_timestamps: 'Fechas demasiado antiguas o inválidas',
  unknown_version: 'Versiones de la app no registradas',
  sessions_without_start: 'Sesiones sin session_start',
  sessions_too_long: 'Sesiones de más de 6 h',
  runs_without_end: 'Runs sin run_end',
  runs_zero_duration: 'Runs con duración 0 pero minutos entre inicio y fin',
  missing_events: 'Eventos perdidos (huecos de secuencia)',
  sdk_dropped: 'Eventos descartados por la cola del SDK',
  purchases_without_product: 'Compras sin producto',
  broken_revenue_currency: 'Ingresos con moneda inválida',
  negative_ad_revenue: 'Ingresos publicitarios negativos',
  ad_revenue_micros: 'Ingreso por impresión demasiado alto (¿micros?)',
  ads_without_revenue: 'Anuncios vistos sin ingresos',
  iap_duplicates: 'Compras repetidas',
  iap_not_validated: 'Compras que no pasaron la validación del recibo',
  iap_without_transaction_id: 'Compras sin id de transacción',
  revenue_without_fx: 'Ingresos sin tipo de cambio',
  derivation_lag: 'Tablas derivadas al día',
  campaign_without_creative_mapping: 'Campañas sin mapeo de creatividades',
  acquisition_connections: 'Conexiones de adquisición',
  connection_errors: 'Conexiones de adquisición con errores',
  connection_stale: 'Datos de adquisición al día',
  install_conversion_actions: 'Acción de conversión de instalación',
  ads_timezone_mismatch: 'Zona horaria de la cuenta de Google Ads',
  spend_without_fx: 'Gasto sin tipo de cambio',
  install_discrepancy: 'Instalaciones de Google Ads frente a las que ve el juego',
  play_reports_stale: 'Informes de Play Console al día',
};


const CHANGE_UNIT: Record<string, 'rate' | 'money' | 'count'> = { cpi: 'money', installs: 'count', dau: 'count', d1: 'rate', revenue: 'money', arpdau: 'money', crash_rate: 'rate' };

function changeValue(metric: string, v: number, ccy: string): string {
  const unit = CHANGE_UNIT[metric] ?? 'count';
  if (unit === 'rate') return fmtPct(v, 1);
  if (unit === 'money') return fmtMoney(v, ccy, v < 1 ? 3 : 2);
  return fmtNum(v, v < 10 ? 1 : 0);
}

/** "El 15 oct" or "Del 13 oct al 15 oct". */
function span(w: { from?: string; to?: string } | undefined): string {
  return w?.from === w?.to ? `El ${fmtDay(w?.from)}` : `Del ${fmtDay(w?.from)} al ${fmtDay(w?.to)}`;
}

/** What an alert says, in Spanish, from its details. */
export function alertText(a: Pick<Alert, 'kind' | 'metric' | 'details' | 'title' | 'subject'>, ccy: string): { headline: string; detail: string } {
  const d = a.details as Record<string, any>;
  switch (a.kind) {
    case 'metric_change': {
      const name = METRIC_ES[a.metric ?? ''] ?? a.metric ?? '';
      const change = d.change === null || d.change === undefined ? 'sube desde 0' : `${d.change > 0 ? '+' : '−'}${fmtNum(Math.abs(d.change) * 100, 0)} %`;
      const p = typeof d.p_value === 'number' ? (d.p_value < 0.0001 ? 'p < 0,0001' : `p = ${fmtNum(d.p_value, 4)}`) : null;
      return {
        headline: `${name} ${change}: ${changeValue(a.metric ?? '', d.value, ccy)} frente a ${changeValue(a.metric ?? '', d.baseline, ccy)}`,
        detail: [`${span(d.window)} frente al ${fmtDay(d.baseline_window?.from)} – ${fmtDay(d.baseline_window?.to)}`, `n = ${fmtNum(d.n, 0)} / ${fmtNum(d.baseline_n, 0)}`, p].filter(Boolean).join(' · '),
      };
    }
    case 'spend_without_installs':
      return {
        headline: `La campaña ${d.campaign ?? d.campaign_id} gastó ${fmtMoney(d.spend, ccy)} sin instalaciones`,
        detail: `${span(d.window)} · umbral ${fmtMoney(d.min_spend, ccy)}. Comprueba la campaña y la acción de conversión de instalación en Google Ads.`,
      };
    case 'creative_fatigue':
      return {
        headline: `La creatividad ${d.name ?? d.creative_id} ${d.status === 'watch' ? 'encarece su CPI' : 'pierde fuerza'}`,
        detail: ((d.signals as string[]) ?? []).map(signalEs).join(' · '),
      };
    case 'data_health':
      return { headline: `Data Health: ${HEALTH_TITLE[a.subject] ?? a.subject} (${d.status === 'warn' ? 'aviso' : 'error'})`, detail: String(d.detail ?? '') };
    default:
      return { headline: a.title, detail: '' };
  }
}

/** How a rule reads ("CPI sube un 30 %: últimos 3 días frente a los 14 anteriores"). */
export function ruleText(r: Pick<AlertRule, 'kind' | 'metric' | 'params'>, ccy: string): string {
  const p = r.params as Record<string, any>;
  switch (r.kind) {
    case 'metric_change': {
      const up = r.metric === 'cpi' || r.metric === 'crash_rate';
      return `${METRIC_ES[r.metric ?? ''] ?? r.metric} ${up ? 'sube' : 'baja'} un ${fmtNum((p.change ?? 0) * 100, 0)} % o más: últimos ${p.recent_days} día(s) frente a los ${p.baseline_days} anteriores${r.metric === 'crash_rate' ? ` (y al menos ${fmtPct(p.min_rate ?? 0.005, 1)})` : ''}`;
    }
    case 'spend_without_installs':
      return `Una campaña gasta ${fmtMoney(p.min_spend, ccy)} o más en ${p.days} día(s) sin instalaciones`;
    case 'creative_fatigue':
      return `Una creatividad muestra fatiga${p.include_watch ? ' (o su CPI sube)' : ''}`;
    case 'data_health':
      return `Data Health: «${HEALTH_TITLE[r.metric ?? ''] ?? r.metric}» en ${p.level === 'warn' ? 'aviso o error' : 'error'}`;
    default:
      return r.kind;
  }
}
